import { useLocalStore } from 'mobx-react-lite';
import { MouseEvent, MutableRefObject, useRef } from 'react';

import Tasks from 'APP/Tasks';
import { OnboardingType } from 'APP/constants/onboarding';
import { PayloadType } from 'APP/model/message/messageModel.types';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { InputPanel } from 'STORE/InputPanelsStore/InputPanel/InputPanel';

import { createTextMessage } from '../Tasks/createTextMessage';
import { createVoiceMessage } from '../Tasks/createVoiceMessage';
import { editTextMessage } from '../Tasks/editTextMessage';
import { editVoiceMessage } from '../Tasks/editVoiceMessage';

interface ISubmitButtonPresenter {
  inputPanel: InputPanel | null;
  group: Group | null;
  isMenuOpen: boolean;
  isSubmitDisabled: boolean;
  isShowMenu: boolean;
  isShowOnboarding: boolean;
  canBeScheduled: boolean;
  isDisabled: boolean;
  isVoiceMode: boolean;
  closeOnboarding(): void;
  editMessage(): Promise<void>;
  createMessage(): Promise<void>;
  onCloseMenu(): void;
  onToggleMenu(event: MouseEvent<HTMLButtonElement>): void;
  onSubmit(): Promise<void>;
  onSetSubmitDisabled(isDisabled: boolean): void;
}

interface IUseSubmitButtonPresenter {
  presenter: ISubmitButtonPresenter;
  submitButtonRef: MutableRefObject<HTMLButtonElement | null>;
}

export const useSubmitButtonPresenter = (): IUseSubmitButtonPresenter => {
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  const presenter = useLocalStore<ISubmitButtonPresenter>(() => ({
    isMenuOpen: false,
    isSubmitDisabled: false,

    get inputPanel(): InputPanel | null {
      return Entities.InputPanelsStore.activeInputPanel;
    },

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get canBeScheduled(): boolean {
      if (!Features.isScheduledMessagesEnabled()) {
        return false;
      }

      return Boolean(
        Entities.GroupsStore.activeGroup?.canBeScheduled && !presenter.inputPanel?.isEditMode
      );
    },

    get isDisabled(): boolean {
      if (!presenter.inputPanel || presenter.isSubmitDisabled) {
        return true;
      }

      return !presenter.isVoiceMode && presenter.inputPanel.isEmptyValue;
    },

    get isShowMenu(): boolean {
      return !presenter.isDisabled && presenter.canBeScheduled && presenter.isMenuOpen;
    },

    get isShowOnboarding(): boolean {
      return presenter.canBeScheduled;
    },

    get isVoiceMode(): boolean {
      if (
        presenter.inputPanel?.editMessage &&
        presenter.inputPanel.editMessage.payload.payloadType === PayloadType.VoiceMessage
      ) {
        return true;
      }

      return Boolean(
        presenter.inputPanel?.voice?.isRecording || presenter.inputPanel?.voice?.isPreviewMode
      );
    },

    closeOnboarding(): void {
      Tasks.app.onboarding.closeOnboarding(OnboardingType.ScheduleMessage);
    },

    async editMessage(): Promise<void> {
      if (!presenter.group || !presenter.inputPanel?.editMessage) {
        return;
      }

      if (presenter.isVoiceMode) {
        await editVoiceMessage(presenter.group.id, presenter.inputPanel.editMessage);
      } else {
        await editTextMessage(presenter.group.id, presenter.inputPanel.editMessage);
      }
    },

    async createMessage(): Promise<void> {
      if (!presenter.group) {
        return;
      }

      if (presenter.isVoiceMode) {
        await createVoiceMessage(presenter.group.id);
      } else {
        await createTextMessage(presenter.group.id);
      }
    },

    onCloseMenu(): void {
      presenter.isMenuOpen = false;
    },

    onToggleMenu(event: MouseEvent<HTMLButtonElement>): void {
      event.preventDefault();
      presenter.closeOnboarding();
      presenter.isMenuOpen = !presenter.isMenuOpen;
    },

    async onSubmit(): Promise<void> {
      presenter.closeOnboarding();
      presenter.onCloseMenu();
      presenter.isSubmitDisabled = true;

      if (presenter.inputPanel?.isEditMode) {
        await presenter.editMessage();
      } else {
        await presenter.createMessage();
      }

      presenter.isSubmitDisabled = false;
    },

    onSetSubmitDisabled(isDisabled: boolean): void {
      presenter.isSubmitDisabled = isDisabled;
    },
  }));

  return { presenter, submitButtonRef };
};
